export const tooltipLine = {
  id: "vertical-line-plugin", //typescript crashes without id
  beforeDraw: function (chart, easing, opt) {
    if (chart.tooltip?._active?.length) {
      const activePoint = chart?.tooltip?._active?.[0]
      const ctx = chart.ctx
      const x = activePoint.element.x
      const topY = chart.scales.y.top
      const bottomY = chart.scales.y.bottom

      // Draw vertical line
      ctx.save()
      ctx.setLineDash([5, 5])
      ctx.beginPath()
      ctx.moveTo(x, topY)
      ctx.lineTo(x, bottomY)
      ctx.lineWidth = 1
      ctx.strokeStyle = opt.theme === "light" ? "#225AEA" : "#225AEA"
      ctx.stroke()
      ctx.restore()
    }
  }
}

export const tooltipLineWithPoints = {
  id: "vertical-line-plugin-with-points",
  afterDraw: function (chart, easing, opt) {
    if (chart.tooltip?._active?.length) {
      const activePoint = chart?.tooltip?._active?.[0]
      const ctx = chart.ctx
      const x = activePoint.element.x
      const topY = chart.scales.y.top
      const bottomY = chart.scales.y.bottom

      // Draw vertical line
      ctx.save()
      ctx.setLineDash([5, 5])
      ctx.beginPath()
      ctx.moveTo(x, topY)
      ctx.lineTo(x, bottomY)
      ctx.lineWidth = 1
      ctx.strokeStyle = opt.theme === "light" ? "#225AEA" : "#225AEA"
      ctx.stroke()
      ctx.restore()

      // Draw points on all datasets at the same x position
      const dataIndex = activePoint.index
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        if (dataset.data[dataIndex] !== null && dataset.data[dataIndex] !== undefined) {
          const meta = chart.getDatasetMeta(datasetIndex)
          const element = meta.data[dataIndex]

          if (element) {
            ctx.save()
            ctx.beginPath()
            ctx.arc(element.x, element.y, 6, 0, 2 * Math.PI)
            ctx.fillStyle = opt.theme === "light" ? "#ffffff" : "#141414"
            ctx.fill()
            ctx.lineWidth = 2
            ctx.strokeStyle = dataset.borderColor
            ctx.stroke()
            ctx.restore()
          }
        }
      })
    }
  }
}

export function getGradient(ctx, chartArea) {
  let width, height, gradient
  const chartWidth = chartArea.right - chartArea.left
  const chartHeight = chartArea.bottom - chartArea.top
  if (!gradient || width !== chartWidth || height !== chartHeight) {
    width = chartWidth
    height = chartHeight
    gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top)
    gradient.addColorStop(0, "rgba(34, 90, 234, 0)")
    gradient.addColorStop(1, "rgba(34, 90, 234, 0.1)")
  }
  return gradient
}

// --- Country flag axis plugin (horizontal "by country" bar charts) ---------------
// Draws each country's rectangular flag immediately to the left of its y-axis label. ISO2 codes
// come from the dataset (`codes`, aligned with `labels`). The SVGs are the 4x3 flag-icons set,
// the exact source react-country-flag already pulls for the Health page and the phone input, so
// the flags here match the ones the rest of the app renders. Images load async and trigger a
// redraw once ready, so they appear without waiting on a hover.
const FLAG_CDN = "https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/4x3/"
const flagImgCache = new Map()

const loadFlagImage = (iso2, onReady) => {
  const key = String(iso2).toLowerCase()
  if (flagImgCache.has(key)) {
    const cached = flagImgCache.get(key)
    // Gate on `complete` only: these SVGs carry a viewBox but no width/height, so they report
    // naturalWidth 0 in some browsers even once loaded. drawImage with explicit destination
    // dimensions rasterizes them regardless, and the onerror -> null path stops broken draws.
    return cached && cached.complete ? cached : null
  }
  const img = new Image()
  // The CDN sends `access-control-allow-origin: *`, so the canvas stays untainted.
  img.crossOrigin = "anonymous"
  img.onload = () => {
    if (onReady) onReady()
  }
  img.onerror = () => flagImgCache.set(key, null)
  img.src = `${FLAG_CDN}${key}.svg`
  flagImgCache.set(key, img)
  return null
}

// Fetches every flag once, up front, and resolves only once all of them have settled
// (loaded or failed) - the caller then triggers exactly one chart.update() itself, from
// outside Chart.js's own draw cycle. This replaces relying on each image's own onload to
// call back into afterDraw: an image that resolves near-instantly (already in the
// browser's HTTP cache - all of these were confirmed to load fine over the network, this
// was never a 404/CORS problem) can fire its onload while afterDraw is still mid-loop
// over the rows below it, and a chart.update() from there re-enters Chart.js's draw cycle
// before the current pass has unwound, restarting the render and dropping whatever that
// pass hadn't reached yet - which read as "only the first couple of flags ever show up".
export const preloadFlags = (codes) => {
  const keys = [...new Set((codes || []).filter(Boolean).map((code) => String(code).toLowerCase()))]
  return Promise.all(
    keys.map(
      (key) =>
        new Promise((resolve) => {
          if (flagImgCache.has(key)) {
            resolve()
            return
          }
          const img = new Image()
          img.crossOrigin = "anonymous"
          // Resolve either way - a flag that fails to load is fine to skip (it just won't
          // draw), not a reason to hang the rest of the batch.
          img.onload = () => resolve()
          img.onerror = () => {
            flagImgCache.set(key, null)
            resolve()
          }
          img.src = `${FLAG_CDN}${key}.svg`
          flagImgCache.set(key, img)
        })
    )
  )
}

const roundedRectPath = (ctx, x, y, w, h, r) => {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

// Exactly 4:3, matching the source viewBox, so nothing is stretched.
const FLAG_WIDTH = 20
const FLAG_HEIGHT = 15
const FLAG_GAP = 8

export const countryFlagAxisPlugin = {
  id: "country-flag-axis",
  afterDraw(chart) {
    const codes = chart?.data?.datasets?.[0]?.codes
    const yAxis = chart?.scales?.y
    if (!codes || !yAxis || !chart.chartArea) return
    const ctx = chart.ctx
    const labels = chart.data.labels || []
    // Labels are left-aligned on the axis (ticks.crossAlign 'far'), so the flags sit in a clean
    // fixed column just left of it, inside the reserved layout.padding.left.
    const flagX = Math.max(2, yAxis.left - FLAG_GAP - FLAG_WIDTH)
    ctx.save()
    labels.forEach((label, i) => {
      const iso2 = codes[i]
      if (!iso2) return
      // Safety net only - the caller (AlertsCountryChart.js) already preloads every code
      // via preloadFlags() and triggers one clean chart.update() once they're all settled,
      // so this normally hits the cache-complete path immediately. This callback only
      // matters if a flag wasn't preloaded for some reason (e.g. codes changed after the
      // preload effect ran); deferred to the next frame so it can't re-enter the draw
      // cycle this afterDraw pass is still in the middle of.
      const img = loadFlagImage(iso2, () => {
        requestAnimationFrame(() => {
          try {
            chart.update("none")
          } catch (e) {
            /* chart gone */
          }
        })
      })
      if (!img) return
      const flagY = yAxis.getPixelForTick(i) - FLAG_HEIGHT / 2
      ctx.save()
      try {
        roundedRectPath(ctx, flagX, flagY, FLAG_WIDTH, FLAG_HEIGHT, 2)
        ctx.clip()
        ctx.drawImage(img, flagX, flagY, FLAG_WIDTH, FLAG_HEIGHT)
      } catch (e) {
        // Some browsers throw drawImage-ing a viewBox-only SVG (no explicit width/height,
        // so its "intrinsic size" is 0x0) despite `.complete` being true - restore still
        // has to run below regardless, or the clip this row just pushed stays applied to
        // every draw after it: every later row (and the next full redraw) would render
        // clipped to this one row's tiny rounded-rect, i.e. invisible.
        console.error("[country-flag-axis] drawImage failed for", iso2, e)
      }
      ctx.restore()
    })
    ctx.restore()
  }
}
